import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useRegisterChartOption } from '@common/contexts/ChartOptionsContext';
import WidgetContainer from '@common/components/WidgetContainer';

/**
 * ChartWidgetContainer
 *
 * Shared wrapper for all ECharts-based dashboard widgets.
 * Handles:
 *  1. Registering the chart option with ChartOptionsContext (for export)
 *  2. Loading / no-data states via WidgetContainer
 *  3. Rendering ReactECharts with sensible defaults
 *
 * Usage (simple):
 *   <ChartWidgetContainer widget={widget} option={option} />
 *
 * Usage (custom render, e.g. overlays or event handlers):
 *   <ChartWidgetContainer widget={widget} option={option}>
 *     <ReactECharts option={option} onEvents={events} ... />
 *   </ChartWidgetContainer>
 */
function ChartWidgetContainer({
  widget,
  option,
  noDataMessage,
  style,
  onEvents,
  children,
}) {
  const { t } = useTranslation();
  const { data } = widget;

  useRegisterChartOption(widget.id, option);

  if (!option) {
    return (
      <WidgetContainer
        loading={data === undefined}
        noData={data !== undefined}
        noDataMessage={noDataMessage || t('dashboard.widgets.noData')}
      />
    );
  }

  // Allow a custom render via children (e.g. StackedBarChartWidget with overlays)
  if (children) {
    return <WidgetContainer>{children}</WidgetContainer>;
  }

  return (
    <WidgetContainer>
      <ReactECharts
        option={option}
        style={{ height: '100%', width: '100%', ...style }}
        opts={{ renderer: 'canvas' }}
        onEvents={onEvents}
      />
    </WidgetContainer>
  );
}

ChartWidgetContainer.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({}),
    ]),
  }).isRequired,
  option: PropTypes.shape({}),
  noDataMessage: PropTypes.string,
  style: PropTypes.shape({}),
  onEvents: PropTypes.shape({}),
  children: PropTypes.node,
};

ChartWidgetContainer.defaultProps = {
  option: null,
  noDataMessage: undefined,
  style: undefined,
  onEvents: undefined,
  children: undefined,
};

export default ChartWidgetContainer;
